import React, { useEffect } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import GoogleComplianceNotice from "./GoogleComplianceNotice";
import { FaShieldAlt, FaCheckCircle, FaClock } from "react-icons/fa";

const ApplyNow: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  const applyLink = import.meta.env.VITE_WEBSITE_LINK || "#";

  // Reusable Application Action Card
  const ApplicationCard = (
    <div className="w-full max-w-[460px]  rounded-[24px] p-6 sm:p-7  flex flex-col gap-5 text-left">
      <div className="pb-4 border-b border-[#E2E8F0]">
        <span className="text-[11px] font-black uppercase tracking-wider text-[#DE3226] block">
          Online Application
        </span>
        <h2 className="text-xl font-black text-neutral-900 mt-0.5">
          Eligibility & Requirements
        </h2>
      </div>

      <div className="flex flex-col gap-3.5 text-xs sm:text-sm text-neutral-700">
        <div className="flex items-start gap-3">
          <FaCheckCircle className="text-[#DE3226] text-sm mt-0.5 flex-shrink-0" />
          <span><strong>Salaried Individuals:</strong> Age 21 to 58 years with regular monthly income.</span>
        </div>
        <div className="flex items-start gap-3">
          <FaCheckCircle className="text-[#DE3226] text-sm mt-0.5 flex-shrink-0" />
          <span><strong>Required Documents:</strong> Valid PAN Card, Aadhaar Card, and Salary Bank Statement.</span>
        </div>
        <div className="flex items-start gap-3">
          <FaCheckCircle className="text-[#DE3226] text-sm mt-0.5 flex-shrink-0" />
          <span><strong>Compliant Tenure:</strong> Strictly 62 to 180 days repayment (minimum 62 days, maximum 180 days).</span>
        </div>
        <div className="flex items-start gap-3">
          <FaCheckCircle className="text-[#DE3226] text-sm mt-0.5 flex-shrink-0" />
          <span><strong>Transparent Fees:</strong> All interest rates, APR, and processing charges disclosed before agreement.</span>
        </div>
      </div>

      <a
        href={applyLink}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full bg-[#DE3226] hover:bg-[#A82A1B] text-white py-4 px-6 rounded-[12px] font-bold text-base md:text-lg flex items-center justify-between transition-all active:scale-95 shadow-md group"
      >
        <span>Continue to Online Application</span>
        <svg
          className="w-5 h-5 transition-transform group-hover:translate-x-1"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
        </svg>
      </a>
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-white text-[var(--text-primary)] relative overflow-x-hidden font-sans">
      <Navbar />

      {/* Hero Section with Responsive Placement */}
      <section className="w-full bg-[#FFFDFB] pt-24 md:pt-28 lg:pt-32 pb-12 md:pb-16 px-6 lg:px-20 flex justify-center border-b border-[#FBE3DF]">
        <div className="max-w-[1400px] w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left Column (7 cols on desktop) */}
          <div className="col-span-1 lg:col-span-7 flex flex-col items-center lg:items-start text-center lg:text-left">
            
            {/* Header Eyebrow */}
            <div className="inline-flex items-center gap-2.5 text-neutral-500 uppercase tracking-[0.15em] text-xs md:text-sm font-bold mb-2">
              <span className="w-7 h-[1.5px] bg-[#DE3226]" />
              <span className="text-[#DE3226]">Fast & Transparent Online Application</span>
            </div>

            {/* Heading */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[46px] font-kualine md:font-sans font-semibold md:font-black leading-[1.12] tracking-tight text-neutral-900 mt-1">
              Apply for your loan,
              <span className="block text-[#DE3226] mt-1.5">Get funded directly to your bank.</span>
            </h1>

            {/* Subheading */}
            <p className="text-neutral-600 font-medium text-sm sm:text-base leading-relaxed mt-4 max-w-xl">
              Hassle-free short-term loan, payday loan, and personal loan options for salaried individuals. Minimal paperwork, transparent tenure from 62 to 180 days, and fast digital processing.
            </p>

            {/* Mobile-Only: Application Card right after heading & description */}
            <div className="flex lg:hidden w-full justify-center mt-6">
              {ApplicationCard}
            </div>

            {/* 3 Metric Cards (Appears after card on mobile, below description on desktop) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 w-full mt-6 text-left">
              <div className="bg-gradient-to-br from-[#FFF5F4] to-white border border-[#FBE3DF] rounded-[16px] p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(222,50,38,0.04)]">
                <div className="w-6 h-6 rounded-lg bg-[#DE3226]/10 text-[#DE3226] flex items-center justify-center text-xs mb-1.5">
                  <FaClock className="w-3 h-3" />
                </div>
                <span className="text-neutral-400 uppercase tracking-wider text-[10px] font-bold block">
                  Loan Tenure
                </span>
                <span className="text-base font-black text-neutral-900 mt-0.5 block">
                  62 to 180 Days
                </span>
                <span className="text-[10px] text-neutral-500 mt-0.5 block">
                  Min 62 – Max 180 Days
                </span>
              </div>

              <div className="bg-gradient-to-br from-[#FFF5F4] to-white border border-[#FBE3DF] rounded-[16px] p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(222,50,38,0.04)]">
                <div className="w-6 h-6 rounded-lg bg-[#DE3226]/10 text-[#DE3226] flex items-center justify-center text-xs mb-1.5">
                  <FaShieldAlt className="w-3 h-3" />
                </div>
                <span className="text-neutral-400 uppercase tracking-wider text-[10px] font-bold block">
                  Maximum APR
                </span>
                <span className="text-base font-black text-[#DE3226] mt-0.5 block">
                  Disclosed Upfront
                </span>
                <span className="text-[10px] text-neutral-500 mt-0.5 block">
                  Separate clear disclosure
                </span>
              </div>

              <div className="bg-gradient-to-br from-[#FFF5F4] to-white border border-[#FBE3DF] rounded-[16px] p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(222,50,38,0.04)]">
                <div className="w-6 h-6 rounded-lg bg-[#DE3226]/10 text-[#DE3226] flex items-center justify-center text-xs mb-1.5">
                  <FaCheckCircle className="w-3 h-3" />
                </div>
                <span className="text-neutral-400 uppercase tracking-wider text-[10px] font-bold block">
                  Disbursal
                </span>
                <span className="text-base font-black text-neutral-900 mt-0.5 block">
                  Within Minutes
                </span>
                <span className="text-[10px] text-neutral-500 mt-0.5 block">
                  Direct to bank transfer
                </span>
              </div>
            </div>

          </div>

          {/* Desktop-Only: Right Column (5 cols) Action Card */}
          <div className="hidden lg:flex col-span-5 w-full justify-end">
            {ApplicationCard}
          </div>

        </div>
      </section>

      {/* Google Ads Compliance Disclosures */}
      <GoogleComplianceNotice />

      <Footer />
    </div>
  );
};

export default ApplyNow;
